import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import classes from "./TalentActivitySummary.module.css";
import {
  getNotifications,
  talentActivitySummary,
  markNotificationAsRead,
} from "../../services/talentCalls";
import { errorHandler } from "../../utils";
import DoughnutChart from "../../components/analytics/analytics";
import Logo from "../../assets/MessageIcons/logo.svg";

const DoughnutChartSection = ({ jobFlowLabels, jobFlowValues, jobStatusLabels, jobStatusValues }) => (
  <div className={classes.chartContainer}>
    <div style={{ width: "300px" }}>
      <DoughnutChart labels={jobFlowLabels} values={jobFlowValues} />
    </div>
    <div style={{ width: "300px" }}>
      <DoughnutChart labels={jobStatusLabels} values={jobStatusValues} />
    </div>
  </div>
);

const NotificationsSection = ({
  notifications,
  onNotificationClick,
  autoMarkAsRead,
  seenAt,
  page,
  total,
  pageSize,
  onPageChange,
}) => {
  const getTalentNotificationContent = (notification) => {
    const { type, title, message, relatedId } = notification;
    switch (type) {
      case "JOB_ALERT":
        const jobTitle = title?.replace("New Job Alert: ", "") || "Job";
        return {
          title,
          message: `Hola! A new ${jobTitle} job with your skills just got posted.`,
          link: `/job/${relatedId || ""}`,
          showActionLink: true,
        };
      case "ONBOARDING_REMINDER":
        return {
          title: "Onboarding Reminder",
          message: "Hello, just a reminder that you haven't completed onboarding.",
          link: "/talent-onboarding?stage=1",
          showActionLink: true,
        };
      case "ONBOARDING_COMPLETED":
        return {
          title: "Onboarding Complete",
          message: "Congratulations, you just completed your onboarding! Thank you.",
          showActionLink: false,
        };
      case "PROFILE_UPDATED":
        return {
          title: "Profile Updated",
          message: "You have just updated your profile.",
          showActionLink: false,
        };
      case "VERIFICATION_SUCCESS":
        return {
          title: "Verification Success",
          message: "Nice! You just got verified! You are now ready to start applying for jobs.",
          link: "/job-feed",
          showActionLink: true,
        };
      case "APPLICATION_ACCEPTED":
        return {
          title: title || "Application Accepted",
          message: message?.replace(/<[^>]*>/g, "") ||
            "A client just accepted your application to their job role. Congratulations!",
          link: "/matches",
          showActionLink: true,
        };
      case "SKILL_DEVELOPMENT":
        return {
          title: "Skill Development",
          message: "Hola, you really need to keep stepping up your game. Check out our recommended self development resources.",
          link: "/talent/self-development?preselected=true",
          showActionLink: true,
        };
      default:
        return {
          title: title || "Notification",
          message: message?.replace(/<[^>]*>/g, "") || "You have a new notification.",
          showActionLink: false,
        };
    }
  };

  const getNotificationStatus = (notification) => {
    if (!notification.isRead) {
      const createdAt = new Date(notification.createdAt).getTime();
      if (createdAt > seenAt) return "new";
      return "unread";
    }
    return null;
  };

  return (
    <div className={classes.messageContainer}>
      <p className={classes.subheading}>Notifications</p>
      <div className={classes.line}></div>

      {notifications.length === 0 ? (
        <div className={classes.emptyState}>No notifications</div>
      ) : (
        <>
          {notifications.map((notification, index) => {
            const content = getTalentNotificationContent(notification);
            const status = getNotificationStatus(notification);

            if (!notification.isRead && !content.showActionLink) {
              autoMarkAsRead(notification);
            }

            return (
              <div key={`notification-${index}`} className={classes.notificationItem} style={{ position: "relative" }}>
                <div className={classes.notificationHeaderRow}>
                  <span className={classes.logoIcon}>
                    <img src={Logo} alt="Logo" />
                  </span>
                  <span className={classes.notificationTitle}>{content.title}</span>
                  {status && (
                    <span
                      className={
                        status === "new"
                          ? classes.statusLabelGreen
                          : status === "unread"
                            ? classes.statusLabelRed
                            : classes.statusLabel
                      }
                    >
                      <span
                        className={
                          status === "new"
                            ? classes.dotGreen
                            : status === "unread"
                              ? classes.dotRed
                              : ""
                        }
                      ></span>
                      <span className={classes.statusText}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </span>
                  )}
                </div>
                <p className={classes.notificationMessage}>{content.message}</p>
                <div className={classes.notificationFooter}>
                  <span className={classes.notificationTime}>
                    {new Date(notification.createdAt).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  {content.showActionLink && (
                    <span className={classes.actionLink} onClick={() => onNotificationClick(notification, content.link)}>
                      View here
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {total > pageSize && (
            <div className={classes.paginationContainer}>
              <Pagination
                pageSize={pageSize}
                onChange={onPageChange}
                current={page}
                total={total}
                nextIcon={<span>›</span>}
                prevIcon={<span>‹</span>}
                showTitle={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

function TalentActivitySummary() {
  const [notifications, setNotifications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [jobStatusValues, setJobStatusValues] = useState([]);
  const [jobFlowValues, setJobFlowValues] = useState([]);
  const [seenAt, setSeenAt] = useState(Date.now());
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(10);

  const navigate = useNavigate();
  const location = useLocation();

  const jobStatusLabels = ["Active Jobs", "Rejected Jobs", "Completed Jobs"];
  const jobFlowLabels = ["Jobs Applied", "Jobs Accepted", "Jobs Hired", "Jobs Viewed"];

  const onlyNotifications = new URLSearchParams(location.search).get("onlyNotifications") === "true";
  const onlyCharts = new URLSearchParams(location.search).get("onlyCharts") === "true";

  const fetchNotifications = async (pageNum = 1) => {
    try {
      const res = await getNotifications(pageNum);
      if (res?.data?.data?.notifications) {
        const data = res.data.data;
        setNotifications(data.notifications);
        setTotalItems(data.total || 0);
        setSeenAt(Date.now());
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler("Network error: Please check your internet connection.", "Error");
    }
  };

  const fetchTalentActivitySummary = async () => {
    try {
      const res = await talentActivitySummary();
      if (res?.data.data) {
        const m = res.data.data?.metrics;
        setMetrics(m);
        setJobStatusValues([m.activeJobs, m.rejectedJobs, m.completedJobs]);
        setJobFlowValues([m.jobsApplied, m.jobsAccepted, m.jobsHired, m.jobsViewed]);
      }
    } catch (error) {
      error.response?.data
        ? errorHandler(error.response.data.errors, "Error")
        : errorHandler("Network error: Please check your internet connection.", "Error");
    }
  };

  const handleNotificationClick = async (notification, link) => {
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification._id);
        setNotifications((prev) =>
          prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n))
        );
      } catch {
        errorHandler("Failed to mark notification as read");
      }
    }
    if (link) navigate(link);
  };

  const autoMarkAsRead = async (notification) => {
    try {
      await markNotificationAsRead(notification._id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n))
      );
    } catch {
      errorHandler("Failed to auto-mark notification as read");
    }
  };

  useEffect(() => {
    fetchNotifications(page);
    if (!onlyNotifications) fetchTalentActivitySummary();
  }, [page]);

  return (
    <div className={classes.container}>
      {!onlyNotifications && <p className={classes.heading}>Activity Summary</p>}
      {(onlyCharts || !onlyNotifications) && (
        <DoughnutChartSection
          jobFlowLabels={jobFlowLabels}
          jobFlowValues={jobFlowValues}
          jobStatusLabels={jobStatusLabels}
          jobStatusValues={jobStatusValues}
        />
      )}
      {!onlyCharts && (
        <NotificationsSection
          notifications={notifications}
          onNotificationClick={handleNotificationClick}
          autoMarkAsRead={autoMarkAsRead}
          seenAt={seenAt}
          page={page}
          total={totalItems}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
        />
      )}
    </div>
  );
}

export default TalentActivitySummary;
